// src/components/block/liveStreamManagment/DetachedChatFeed.tsx
// Fil du chat de la chaîne, copie de celui de l'onglet Modération (DecisionFeed), pour l'onglet
// détaché du multiview. Il ne suit que la chaîne de la régie et n'écrit rien dans la sélection
// de chaînes mémorisée par l'écran Modération.

import React, { useCallback, useEffect, useRef, useState } from 'react';

import DecisionFeed from '../chatModerationManagment/DecisionFeed';
import styles       from '../chatModerationManagment/ChatModeration.module.scss';
import { channelColor } from '../chatModerationManagment/chatModerationUi';
import { fetchChatModMessages, decideChatMessages } from '../../../backendServices/moderationManagment/api_chat';
import { ChatDecision, ChatModMessage } from '../../../dataTypes/moderationManagment/chat_types';
import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState        } from '../../../store/generalReducer';

const EVERY_MS = 3_000;

interface Props {
  channelId   : number;
  channelName : string;
}

export const DetachedChatFeed: React.FC<Props> = ({ channelId, channelName }) => {
  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const adminId = authentication.admin?.id ?? null;

  const [recent, setRecent] = useState<ChatModMessage[]>([]);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (adminId === null || inFlight.current) return;
    inFlight.current = true;
    try {
      const data = await fetchChatModMessages(adminId, [channelId]);
      setRecent(data.recent);
    } catch { /* le relevé suivant reprend */ }
    finally { inFlight.current = false; }
  }, [adminId, channelId]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, EVERY_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const onDecide = useCallback(async (id: number, action: ChatDecision) => {
    if (adminId === null) return;
    try { await decideChatMessages(adminId, [id], action); } catch { /* idem */ }
    load();
  }, [adminId, load]);

  // `boardWrap` porte les variables de couleur de l'écran Modération. Le panneau a `grid-area: feed`
  // (une zone de la grille de cet écran, absente d'ici : il partait dans une case implicite, en bas à
  // droite, à la taille de son contenu) : on l'annule et on l'étire sur toute la place disponible.
  return (
    <div data-chat-feed className={styles.boardWrap}
         style={{ position: 'absolute', inset: 0, height: 'auto', minHeight: 0, gap: 0, display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr)', gridTemplateRows: 'minmax(0, 1fr)' }}>
      <style>{`[data-chat-feed] > section { grid-area: auto; width: 100%; height: 100%; min-width: 0; min-height: 0; }`}</style>
      <DecisionFeed recent={recent} nameOf={() => channelName} colorOf={id => channelColor(id, [channelId])} showChannel={false} onDecide={onDecide} />
    </div>
  );
};
